import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, resolveDirective as _resolveDirective, withDirectives as _withDirectives, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { key: 0 }
const _hoisted_2 = { class: "rounded-lg justify-space-between mb-4 pa-2 d-flex" }
const _hoisted_3 = { class: "d-flex align-center" }
const _hoisted_4 = { class: "text-white text-subtitle-2 font-weight-bold" }
const _hoisted_5 = { class: "font-weight-medium" }
const _hoisted_6 = { class: "text-caption text-medium-emphasis" }
const _hoisted_7 = { class: "text-body-2" }
const _hoisted_8 = { class: "text-body-2" }
const _hoisted_9 = { class: "d-flex gap-1" }
const _hoisted_10 = { class: "text-center pt-2" }
const _hoisted_11 = {
  class: "sticky-header bg-grey-lighten-4 d-flex justify-space-between align-center px-4 py-3",
  style: {"border-bottom":"1px solid #e0e0e0"}
}
const _hoisted_12 = { class: "text-h6 font-weight-medium" }
const _hoisted_13 = { class: "pa-6" }

import { Company } from './models/company'
import { ref, onMounted, computed } from 'vue'
import { useUserStore } from './store'

// ─── STORES ────────────────────────────────────────────────────────────────
const itemsPerPage = 10

export default /*@__PURE__*/_defineComponent({
  __name: 'CompaniesView',
  setup(__props) {

// ─── IMPORTS ────────────────────────────────────────────────────────────────
const userStore = useUserStore()

// ─── REFS: UI Flags and Data ───────────────────────────────────────────────
const loading = ref(false)
const activeEditCompany = ref(false)
const activeCreateNewCompany = ref(false)
const loadingCreateCompany = ref(false)
const loadingEditCompany = ref(false)
const searchCompanies = ref('')
const page = ref(1)
const formTouched = ref(false)
const formRef = ref()
const formValid = ref(false)

const headers = [
  { title: 'CÓDIGO', key: 'code' },
  { title: 'NOMBRE', key: 'name' },
  { title: 'NOMBRE EXTENDIDO', key: 'extendedName' },
  { title: 'CREADO EN', key: 'createdAt' },
  { title: 'ACCIONES', key: 'actions' },
]

// ─── REFS: Company ──────────────────────────────────────────────────────────
const companies = ref<Company[]>([])
const company = ref<Company>(new Company())

// ─── ALERTAS ───────────────────────────────────────────────────────────────
const activeAlert = ref(false)
const textAlert = ref('')
const colorAlert = ref<'error' | 'success' | 'info' | 'warning'>('error')

// ─── VALIDACIONES ──────────────────────────────────────────────────────────
const codeError = ref('')
const nameError = ref('')

// ─── COMPUTED ──────────────────────────────────────────────────────────────
const pageCount = computed(() => Math.ceil(companies.value.length / itemsPerPage))

// ─── FUNCIONES AUXILIARES ──────────────────────────────────────────────────
const resetForm = () => {
  company.value = new Company()
  codeError.value = ''
  nameError.value = ''
  formTouched.value = false
}

const formatDate = (date: Date) => {
  return new Intl.DateTimeFormat('es-ES', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date)
}

// ─── EVENTOS / BOTONES ─────────────────────────────────────────────────────
const getCompanies = async () => {
  loading.value = true
  try {
    companies.value = await userStore.getCompanies()
  } catch (error) {
    activeAlert.value = true
    textAlert.value = 'Error al obtener las empresas'
    colorAlert.value = 'error'
  }
  loading.value = false
}

const btnSetActiveCreateCompany = () => {
  activeCreateNewCompany.value = !activeCreateNewCompany.value
  company.value = new Company()
  codeError.value = ''
  nameError.value = ''
}

const validateForm = (): boolean => {
  let isValid = true

  // Validación de campos obligatorios
  if (!company.value.code) {
    codeError.value = 'Código requerido'
    isValid = false
  } else {
    codeError.value = ''
  }

  if (!company.value.name) {
    nameError.value = 'Nombre requerido'
    isValid = false
  } else {
    nameError.value = ''
  }

  if (!isValid) {
    activeAlert.value = true
    textAlert.value = 'Todos los campos obligatorios deben ser completados'
    colorAlert.value = 'error'
  }

  return isValid
}

const btnSetActiveEditCompany = async (selectedCompany: Company) => {
  company.value = new Company(selectedCompany)
  activeEditCompany.value = true
}

const btnCreateCompany = async () => {
  formTouched.value = true

  const valid = await formRef.value?.validate()
  if (!valid.valid || !validateForm()) return

  loadingCreateCompany.value = true
  
  try {
    await userStore.postCompany(company.value)
    activeAlert.value = true
    textAlert.value = 'Empresa creada exitosamente'
    colorAlert.value = 'success'
    activeCreateNewCompany.value = false
    getCompanies()
  } catch (error) {
    activeAlert.value = true
    textAlert.value = 'Error al crear la empresa'
    colorAlert.value = 'error'
  }
  
  loadingCreateCompany.value = false
}

const btnUpdateCompany = async () => {
  formTouched.value = true

  const valid = await formRef.value?.validate()
  if (!valid.valid || !validateForm()) return

  loadingEditCompany.value = true
  
  try {
    await userStore.putCompany(company.value)
    activeAlert.value = true
    textAlert.value = 'Empresa actualizada exitosamente'
    colorAlert.value = 'success'
    activeEditCompany.value = false
    getCompanies()
  } catch (error) {
    activeAlert.value = true
    textAlert.value = 'Error al actualizar la empresa'
    colorAlert.value = 'error'
  }
  
  loadingEditCompany.value = false
}

const btnDeleteCompany = async (companyToDelete: Company) => {
  if (confirm(`¿Está seguro de eliminar la empresa "${companyToDelete.name}"?`)) {
    try {
      await userStore.deleteCompany(companyToDelete.id)
      activeAlert.value = true
      textAlert.value = 'Empresa eliminada exitosamente'
      colorAlert.value = 'success'
      getCompanies()
    } catch (error) {
      activeAlert.value = true
      textAlert.value = 'Error al eliminar la empresa'
      colorAlert.value = 'error'
    }
  }
}

// ─── LIFECYCLE ─────────────────────────────────────────────────────────────
onMounted(async () => {
  // Cargar empresas desde el store
  await getCompanies()
})

return (_ctx: any,_cache: any) => {
  const _component_v_alert = _resolveComponent("v-alert")!
  const _component_v_card_title = _resolveComponent("v-card-title")!
  const _component_v_spacer = _resolveComponent("v-spacer")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_text_field = _resolveComponent("v-text-field")!
  const _component_v_skeleton_loader = _resolveComponent("v-skeleton-loader")!
  const _component_v_avatar = _resolveComponent("v-avatar")!
  const _component_v_pagination = _resolveComponent("v-pagination")!
  const _component_v_data_table = _resolveComponent("v-data-table")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_col = _resolveComponent("v-col")!
  const _component_v_row = _resolveComponent("v-row")!
  const _component_v_form = _resolveComponent("v-form")!
  const _component_v_sheet = _resolveComponent("v-sheet")!
  const _directive_tooltip = _resolveDirective("tooltip")!

  return (_openBlock(), _createBlock(_component_v_card, {
    elevation: "0",
    class: "bg-background pa-5 rounded-lg"
  }, {
    default: _withCtx(() => [
      _createVNode(_component_v_alert, {
        modelValue: activeAlert.value,
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((activeAlert).value = $event)),
        type: colorAlert.value,
        dismissible: "",
        class: "mb-4"
      }, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(textAlert.value), 1)
        ]),
        _: 1
      }, 8, ["modelValue", "type"]),
      (!activeCreateNewCompany.value && !activeEditCompany.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
            _createVNode(_component_v_card, {
              class: "mt-3 rounded-lg",
              elevation: "3"
            }, {
              default: _withCtx(() => [
                _createElementVNode("div", _hoisted_2, [
                  _createVNode(_component_v_card_title, {
                    color: "primary",
                    class: "text-subtitle-4"
                  }, {
                    default: _withCtx(() => _cache[11] || (_cache[11] = [
                      _createTextVNode("Empresas")
                    ])),
                    _: 1
                  }),
                  _createVNode(_component_v_spacer),
                  (!activeCreateNewCompany.value && !activeEditCompany.value)
                    ? (_openBlock(), _createBlock(_component_v_btn, {
                        key: 0,
                        onClick: btnSetActiveCreateCompany,
                        variant: "text",
                        text: "Nueva empresa"
                      }))
                    : (_openBlock(), _createBlock(_component_v_btn, {
                        key: 1,
                        onClick: _cache[1] || (_cache[1] = ($event: any) => ((activeCreateNewCompany.value = false), (activeEditCompany.value = false))),
                        variant: "text",
                        text: "Volver a las empresas"
                      }))
                ]),
                _createVNode(_component_v_text_field, {
                  modelValue: searchCompanies.value,
                  "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((searchCompanies).value = $event)),
                  "append-inner-icon": "mdi-magnify",
                  density: "compact",
                  label: "Buscar empresa",
                  variant: "solo",
                  "hide-details": "",
                  "single-line": "",
                  color: "surface-light",
                  class: "mx-4",
                  "bg-color": "background"
                }, null, 8, ["modelValue"]),
                _createVNode(_component_v_data_table, {
                  class: "rounded-lg text-body-2",
                  page: page.value,
                  "onUpdate:page": _cache[4] || (_cache[4] = ($event: any) => ((page).value = $event)),
                  headers: headers,
                  items: companies.value,
                  search: searchCompanies.value,
                  loading: loading.value,
                  "items-per-page": itemsPerPage
                }, {
                  loading: _withCtx(() => [
                    _createVNode(_component_v_skeleton_loader, {
                      class: "bg-background",
                      type: "table-row@10"
                    })
                  ]),
                  [`item.name`]: _withCtx(({ item }) => [
                    _createElementVNode("div", _hoisted_3, [
                      _createVNode(_component_v_avatar, {
                        size: "36",
                        class: "mr-3",
                        color: "primary"
                      }, {
                        default: _withCtx(() => [
                          _createElementVNode("span", _hoisted_4, _toDisplayString(item.code?.substring(0, 2).toUpperCase()), 1)
                        ]),
                        _: 2
                      }, 1024),
                      _createElementVNode("div", null, [
                        _createElementVNode("div", _hoisted_5, _toDisplayString(item.name), 1),
                        _createElementVNode("div", _hoisted_6, _toDisplayString(item.code), 1)
                      ])
                    ])
                  ]),
                  [`item.extendedName`]: _withCtx(({ item }) => [
                    _createElementVNode("div", _hoisted_7, _toDisplayString(item.extendedName || 'N/A'), 1)
                  ]),
                  [`item.createdAt`]: _withCtx(({ item }) => [
                    _createElementVNode("div", _hoisted_8, _toDisplayString(formatDate(item.createdAt)), 1)
                  ]),
                  [`item.actions`]: _withCtx(({ item }) => [
                    _createElementVNode("div", _hoisted_9, [
                      _withDirectives(_createVNode(_component_v_btn, {
                        variant: "text",
                        onClick: ($event: any) => (btnSetActiveEditCompany(item)),
                        icon: "mdi-pencil-outline"
                      }, null, 8, ["onClick"]), [
                        [_directive_tooltip, `Editar`]
                      ]),
                      _withDirectives(_createVNode(_component_v_btn, {
                        variant: "text",
                        onClick: ($event: any) => (btnDeleteCompany(item)),
                        icon: "mdi-delete-outline",
                        color: "error"
                      }, null, 8, ["onClick"]), [
                        [_directive_tooltip, `Eliminar`]
                      ])
                    ])
                  ]),
                  bottom: _withCtx(() => [
                    _createElementVNode("div", _hoisted_10, [
                      _createVNode(_component_v_pagination, {
                        modelValue: page.value,
                        "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((page).value = $event)),
                        length: pageCount.value
                      }, null, 8, ["modelValue", "length"])
                    ])
                  ]),
                  _: 2
                }, 1032, ["page", "items", "search", "loading"])
              ]),
              _: 1
            })
          ]))
        : _createCommentVNode("", true),
      (activeCreateNewCompany.value || activeEditCompany.value)
        ? (_openBlock(), _createBlock(_component_v_row, {
            key: 1,
            justify: "center"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_v_col, {
                cols: "12",
                md: "10",
                lg: "8"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_card, {
                    elevation: "4",
                    class: "bg-background pa-0 ma-0 rounded-lg content-container"
                  }, {
                    default: _withCtx(() => [
                      _createElementVNode("div", _hoisted_11, [
                        _createElementVNode("span", _hoisted_12, _toDisplayString(activeCreateNewCompany.value ? 'Crear nueva empresa' : 'Editar empresa'), 1),
                        _createElementVNode("div", null, [
                          _createVNode(_component_v_btn, {
                            color: "red",
                            variant: "tonal",
                            class: "mr-2",
                            rounded: "lg",
                            onClick: resetForm
                          }, {
                            default: _withCtx(() => _cache[12] || (_cache[12] = [
                              _createTextVNode(" Limpiar ")
                            ])),
                            _: 1
                          }),
                          _createVNode(_component_v_btn, {
                            color: "primary",
                            variant: "tonal",
                            class: "mr-2",
                            rounded: "lg",
                            onClick: _cache[5] || (_cache[5] = ($event: any) => (activeCreateNewCompany.value = false, activeEditCompany.value = false))
                          }, {
                            default: _withCtx(() => _cache[13] || (_cache[13] = [
                              _createTextVNode(" Volver ")
                            ])),
                            _: 1
                          }),
                          _createVNode(_component_v_btn, {
                            color: "primary",
                            class: "text-white",
                            rounded: "lg",
                            loading: loadingCreateCompany.value || loadingEditCompany.value,
                            onClick: _cache[6] || (_cache[6] = ($event: any) => (activeCreateNewCompany.value ? btnCreateCompany() : btnUpdateCompany()))
                          }, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(activeCreateNewCompany.value ? 'Crear' : 'Actualizar'), 1)
                            ]),
                            _: 1
                          }, 8, ["loading"])
                        ])
                      ]),
                      _createVNode(_component_v_row, { justify: "center" }, {
                        default: _withCtx(() => [
                          _createVNode(_component_v_col, { cols: "12" }, {
                            default: _withCtx(() => [
                              _createVNode(_component_v_sheet, {
                                color: "white",
                                class: "pa-6",
                                elevation: "0"
                              }, {
                                default: _withCtx(() => [
                                  _createVNode(_component_v_form, {
                                    ref_key: "formRef",
                                    ref: formRef,
                                    modelValue: formValid.value,
                                    "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((formValid).value = $event)),
                                    "validate-on": "submit lazy"
                                  }, {
                                    default: _withCtx(() => [
                                      _createElementVNode("div", _hoisted_13, [
                                        _cache[14] || (_cache[14] = _createElementVNode("h4", { class: "text-h6 mb-6" }, "Información de la empresa", -1)),
                                        _createVNode(_component_v_row, null, {
                                          default: _withCtx(() => [
                                            _createVNode(_component_v_col, {
                                              cols: "12",
                                              md: "6",
                                              class: "mb-3"
                                            }, {
                                              default: _withCtx(() => [
                                                _createVNode(_component_v_text_field, {
                                                  modelValue: company.value.code,
                                                  "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((company.value.code) = $event)),
                                                  label: "Código *",
                                                  "error-messages": formTouched.value && codeError.value ? [codeError.value] : [],
                                                  variant: "outlined",
                                                  density: "comfortable",
                                                  "bg-color": "white",
                                                  hint: "Código único de la empresa",
                                                  "persistent-hint": ""
                                                }, null, 8, ["modelValue", "error-messages"])
                                              ]),
                                              _: 1
                                            }),
                                            _createVNode(_component_v_col, {
                                              cols: "12",
                                              md: "6",
                                              class: "mb-3"
                                            }, {
                                              default: _withCtx(() => [
                                                _createVNode(_component_v_text_field, {
                                                  modelValue: company.value.name,
                                                  "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((company.value.name) = $event)),
                                                  label: "Nombre *",
                                                  "error-messages": formTouched.value && nameError.value ? [nameError.value] : [],
                                                  variant: "outlined",
                                                  density: "comfortable",
                                                  "bg-color": "white",
                                                  hint: "Nombre de la empresa",
                                                  "persistent-hint": ""
                                                }, null, 8, ["modelValue", "error-messages"])
                                              ]),
                                              _: 1
                                            }),
                                            _createVNode(_component_v_col, {
                                              cols: "12",
                                              class: "mb-3"
                                            }, {
                                              default: _withCtx(() => [
                                                _createVNode(_component_v_text_field, {
                                                  modelValue: company.value.extendedName,
                                                  "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((company.value.extendedName) = $event)),
                                                  label: "Nombre extendido",
                                                  variant: "outlined",
                                                  density: "comfortable",
                                                  "bg-color": "white",
                                                  hint: "Nombre completo o razón social (opcional)",
                                                  "persistent-hint": ""
                                                }, null, 8, ["modelValue"])
                                              ]),
                                              _: 1
                                            })
                                          ]),
                                          _: 1
                                        }),
                                        _cache[15] || (_cache[15] = _createElementVNode("div", { class: "text-caption text-medium-emphasis mt-4" }, " * Campos obligatorios ", -1))
                                      ])
                                    ]),
                                    _: 1
                                  }, 8, ["modelValue"])
                                ]),
                                _: 1
                              })
                            ]),
                            _: 1
                          })
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              })
            ]),
            _: 1
          }))
        : _createCommentVNode("", true)
    ]),
    _: 1
  }))
}
}

})